import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { fetchGhostPosts } from '@/shared/lib/ghost'
import { BOARD_TABS, PAGE_SIZE, boardTabOf } from '@/public/features/board/constants/boardTab'
import { BoardPagination } from '@/public/features/board/ui/BoardPagination'
import { Tap } from '@/shared/ui/Tap'
import { TextField } from '@/shared/ui/TextField'

export function BoardListPage() {
  const [params, setParams] = useSearchParams()
  const tab = boardTabOf(params.get('tag'))
  const q = params.get('q') ?? ''
  const page = Number(params.get('page') ?? '1')

  // 입력 중인 글자는 주소에 넣지 않는다 — 제출해야 목록이 바뀐다.
  const [draft, setDraft] = useState(q)

  const posts = useQuery({
    queryKey: ['ghost-posts', tab.tag, q, page],
    queryFn: () => fetchGhostPosts(tab.tag, q, page, PAGE_SIZE),
  })

  // `replace`를 쓰지 않는다 — 탭·검색·쪽 이동은 사용자의 이동이라 뒤로가기로 되돌아갈 수 있어야 한다.
  const go = (next: { tag?: string; q?: string; page?: number }) => {
    const tag = next.tag ?? tab.tag
    const keyword = next.q ?? q
    const nextPage = next.page ?? 1

    setParams({
      tag,
      ...(keyword === '' ? {} : { q: keyword }),
      ...(nextPage === 1 ? {} : { page: String(nextPage) }),
    })
  }

  const pagination = posts.data?.meta.pagination

  const body = (
    <>
      <h1 className="m-0 mb-7 text-headline-sm font-bold tracking-[-0.02em]">{tab.label}</h1>

      <div className="mb-4 flex flex-wrap gap-2">
        {BOARD_TABS.map((t) => (
          <Tap
            key={t.tag}
            selected={t.tag === tab.tag}
            onClick={() => {
              setDraft('')
              go({ tag: t.tag, q: '' })
            }}
          >
            {t.label}
          </Tap>
        ))}
      </div>

      <form
        className="mb-5 max-w-[360px]"
        onSubmit={(e) => {
          e.preventDefault()
          go({ q: draft })
        }}
      >
        {/* `type="search"` 는 브라우저가 자기 지우기 단추를 그려 `onClear` 와 ×가 두 개 된다. */}
        <TextField
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          // 빈 칸에는 지우기 단추를 붙이지 않는다 — `TextField` 는 `onClear` 만 있으면 늘 그린다.
          {...(draft === ''
            ? {}
            : {
                onClear: () => {
                  setDraft('')
                  go({ q: '' })
                },
              })}
          placeholder="제목 검색"
          aria-label="제목 검색"
        />
      </form>

      <QueryState
        isPending={posts.isPending}
        error={posts.error}
        isEmpty={posts.data?.posts.length === 0}
        emptyMessage={q === '' ? '등록된 글이 없습니다.' : '검색 결과가 없습니다.'}
      >
        <p className="text-black-100 text-label" style={{ marginBottom: 12 }}>
          {pagination?.total}건
        </p>
        <div className="flex flex-col gap-2.5">
          {posts.data?.posts.map((p) => (
            // 카드 모양은 시민 문제·해결카드 목록과 맞춘다.
            <Link
              key={p.id}
              className="block rounded-[14px] border border-white-200 bg-white-50 px-5 py-4 text-inherit shadow-[0_0_4px_-1px_rgb(0_0_0/6%),0_0_12px_-4px_rgb(0_0_0/12%)]"
              to={`/board/${p.slug}`}
            >
              <div className="mb-1.5 text-label text-black-100">
                {p.published_at?.slice(0, 10) ?? ''}
              </div>
              <h3 className="m-0 mb-1.5 text-body-lg leading-[1.4] font-semibold text-black-600">
                {p.title}
              </h3>
              {/* Ghost 의 excerpt 는 본문 앞부분을 그대로 잘라 와 길다 — 두 줄에서 끊는다. */}
              <p className="m-0 line-clamp-2 text-body-sm text-black-100">{p.excerpt}</p>
            </Link>
          ))}
        </div>

        <BoardPagination
          page={pagination?.page ?? 1}
          pages={pagination?.pages ?? 1}
          onChange={(next) => go({ page: next })}
        />
      </QueryState>
    </>
  )

  return (
    <>
      <DefaultLayout className="pc:hidden">
        <div className="layout-body px-4 pt-4 pb-10">{body}</div>
      </DefaultLayout>
      <DefaultLayout tree="pc" className="[--body-w:1000px]">
        <div className="layout-body pt-8 pb-[60px]">{body}</div>
      </DefaultLayout>
    </>
  )
}
